import { createAction } from '@reduxjs/toolkit'
import isNull from 'lodash/isNull'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'

import { sortDrawableLocationsToHaveAreasBelow } from '../utils/sortDrawableLocationsToHaveAreasBelow'
import drawConflictByType from './drawConflictByType'
import { RedrawShapeStoreState, selectRedrawState } from './handleUpdateOpeningGroupsSuccess'
import { drawAndLabelPaths, selectMarkupState } from './markupDocument'
import { IMaterialModificationConflict } from '../../../models/masterSetPlan'
import { DRAWABLE_TYPES, DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Cutout, Overlay, RegionTool, Workspace } from '../../lib/toolBoxes/2D'
import { determineIfMultiSelectable } from '../../lib/utils/functionality-bindings/addSelectFunctionalityToDrawable'
import addSelectFunctionalityToModifications from '../../lib/utils/functionality-bindings/addSelectFunctionalityToModifications'
import { GeometryVisibilityRecord, updateGeometryVisibility } from '../../slices/geometry'
import { setConflictedMaterialOptionModifications, updateConflictResolutionFlag } from '../../slices/masterSetPlan'
import { IMUP2DDrawableLocation, REGION_ENUMS } from '../../types'

export const highlightOptionConflictsForOverlaysAction = createAction<number[]>('highlightOptionConflictsForOverlays')

type MaterialModificationConflictWithShape = IMaterialModificationConflict & {
    shapeType: DRAWING_TYPES
    opening_group_id: number
}

// Collapses one location's conflict, which can appear once per overlay that references it, down
// to a single entry with every overlay's option_ids merged in.
export function mergeConflictsByMaterialLocation(
    conflicts: MaterialModificationConflictWithShape[]
): MaterialModificationConflictWithShape[] {
    const byLocation = new Map<number, MaterialModificationConflictWithShape>()

    for (const conflict of conflicts) {
        const existing = byLocation.get(conflict.material_location_id)

        if (existing) {
            existing.option_ids = Array.from(new Set([...existing.option_ids, ...conflict.option_ids]))
        } else {
            byLocation.set(conflict.material_location_id, { ...conflict })
        }
    }

    return Array.from(byLocation.values())
}

export function* drawOverlappingDrawableLocationsModifications(
    overlappedDrawableLocation: IMUP2DDrawableLocation[],
    overlayPaperRasterIds: number[],
    areaOpacity: number,
    lineOpacity: number,
    regionPaths: paper.Path[],
    alreadyDrawnMaterialLocations: Map<number, paper.Item> = new Map()
) {
    const materialModificationConflicts: (IMaterialModificationConflict & {
        shapeType: DRAWING_TYPES
        opening_group_id: number
    })[] = overlappedDrawableLocation
        // sorting locations that AREAS will be drawn first
        .sort(sortDrawableLocationsToHaveAreasBelow)
        .map((location) => {
            return {
                id: Number(location.opening_location_id + '0000'), // temporary ID
                material_location_id: location.opening_location_id,
                cutouts: location.cutouts,
                coordinates: location.coordinates,
                floor_plan_id: location.document_chunk_id,
                option_ids: overlayPaperRasterIds,
                shapeType: location.shapeType ?? DRAWING_TYPES.SECTION,
                opening_group_id: location.opening_group_id,
            }
        })

    // A location conflicting with 2+ overlays only needs drawing once -- every other overlay just
    // merges its id into that same path's option_ids instead of drawing a duplicate.
    const conflictsToDraw: typeof materialModificationConflicts = []

    materialModificationConflicts.forEach((conflict) => {
        const existingPath = alreadyDrawnMaterialLocations.get(conflict.material_location_id)

        if (!existingPath) {
            conflictsToDraw.push(conflict)

            return
        }

        const existingConflict = existingPath.data.materialModificationConflict as IMaterialModificationConflict

        existingConflict.option_ids = Array.from(new Set([...existingConflict.option_ids, ...overlayPaperRasterIds]))
    })

    // draw conflicts
    const drawnPaths: (paper.Path | null)[] = yield all(
        conflictsToDraw.map((materialConflict) => {
            return call(
                drawConflictByType,
                materialConflict,
                areaOpacity,
                lineOpacity,
                regionPaths,
                addSelectFunctionalityToModifications
            )
        })
    )

    conflictsToDraw.forEach((conflict, index) => {
        const path = drawnPaths[index]

        if (path) alreadyDrawnMaterialLocations.set(conflict.material_location_id, path)
    })

    // Return the merged entry from the map (falls back to the single-id draft if nothing was
    // actually drawn for it).
    return materialModificationConflicts.map((conflict) => {
        const drawnPath = alreadyDrawnMaterialLocations.get(conflict.material_location_id)

        return (drawnPath?.data.materialModificationConflict as typeof conflict | undefined) ?? conflict
    })
}

// Computes and draws one overlay's conflicts against the base home's materials. Null means the
// manager/active floor weren't ready.
export type ProcessOverlayConflictsResult = {
    conflicts: MaterialModificationConflictWithShape[]
    hasConflicts: boolean
    // Collected across the batch so materials overlapping any active overlay are shown.
    overlappingDrawableIds?: number[]
}

export function* processOverlayConflicts(
    manager: PaperManager,
    overlayPaperRasterId: number,
    alreadyDrawnMaterialLocations: Map<number, paper.Item> = new Map()
): Generator<unknown, ProcessOverlayConflictsResult | null, any> {
    const [overlayTool, workspaceTool, regionTool, cutoutTool]: [Overlay, Workspace, RegionTool, Cutout] = yield call(
        manager.getTools,
        [Overlay.NAME, Workspace.NAME, RegionTool.NAME, Cutout.NAME]
    )
    const { areaOpacity, lineOpacity }: RedrawShapeStoreState = yield select(selectRedrawState)

    const { activeFloor, drawableLocations }: ReturnType<typeof selectMarkupState> = yield select(selectMarkupState)

    if (isNull(activeFloor)) return null

    const allDrawableLocationOnDocument: IMUP2DDrawableLocation[] = yield drawableLocations.filter(
        ({ document_chunk_id, drawing_type }) =>
            document_chunk_id === activeFloor.document_chunk.id &&
            drawing_type !== DRAWABLE_TYPES.NOTE &&
            drawing_type !== DRAWABLE_TYPES.HIGHLIGHTER
    )

    // collect all overlapped Locations
    const overlappedDrawableLocation: IMUP2DDrawableLocation[] = allDrawableLocationOnDocument.filter((location) => {
        const item = workspaceTool.getItemWithOpeningLocationId(location.opening_location_id)
        const isPossibleKingStud = location.drawing_type === DRAWABLE_TYPES.WALL

        return (
            item &&
            location.drawing_type !== DRAWABLE_TYPES.HIGHLIGHTER &&
            location.drawing_type !== DRAWABLE_TYPES.NOTE &&
            // We want to be able to delete king studs
            // but those are not multiselectable
            (determineIfMultiSelectable(item) || isPossibleKingStud) &&
            overlayTool.isPathInsideOverlay(item, overlayPaperRasterId)
        )
    })

    if (!overlappedDrawableLocation.length) {
        const regionPaths = workspaceTool.getItemsWithCriteria(
            'data',
            (data) => data.shapeType === REGION_ENUMS.TYPE
        ) as paper.Path[]

        yield call(
            drawAndLabelPaths,
            overlayPaperRasterId,
            { lineOpacity, areaOpacity },
            drawableLocations.filter(({ document_chunk_id }) => document_chunk_id === overlayPaperRasterId),
            regionPaths,
            workspaceTool,
            regionTool
        )

        const alreadyDrawnItems: paper.Item[] = yield call(
            workspaceTool.getItemsWithCriteria,
            'data',
            (data) => data?.drawable_id || data?.aiSuggestion?.id
        )

        alreadyDrawnItems.forEach((item) => {
            if (item.data.document_chunk_id === activeFloor.document_chunk.id) {
                item.data.selectable = false
                item.data.isMultiSelectable = false
            }
        })

        return { conflicts: [], hasConflicts: false }
    }

    //hide  all already Drawn items before
    const alreadyDrawnItemsToHide: paper.Item[] = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data) => data?.drawable_id || data?.aiSuggestion?.id
    )

    alreadyDrawnItemsToHide.forEach((item) => {
        item.visible = false
        item.data.selectable = false
    })

    const regionPaths: paper.Path[] = yield call(workspaceTool.getAllRegionItems)

    const materialModificationConflicts: MaterialModificationConflictWithShape[] = yield call(
        drawOverlappingDrawableLocationsModifications,
        overlappedDrawableLocation,
        [overlayPaperRasterId],
        areaOpacity,
        lineOpacity,
        regionPaths,
        alreadyDrawnMaterialLocations
    )

    const overlappingDrawableIds = overlappedDrawableLocation.map((loc) => loc.drawable_id)

    yield call([cutoutTool, 'setItemMetadataUpdateFunction'], addSelectFunctionalityToModifications)

    return {
        conflicts: materialModificationConflicts,
        hasConflicts: true,
        overlappingDrawableIds,
    }
}

// Processes a locked-together batch of overlays' conflicts together, since dispatching
// setConflictedMaterialOptionModifications per overlay would replace rather than accumulate.
// Sequential (not parallel) so overlapping overlays don't fight over the same paper.js items.
export function* handleHighlightOptionConflictsForOverlays(
    action: ReturnType<typeof highlightOptionConflictsForOverlaysAction>
): Generator<unknown, void | null, any> {
    const overlayIds = action.payload

    if (!overlayIds.length) return

    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (isNull(manager)) return null

    const [workspaceTool]: [Workspace] = yield call(manager.getTools, [Workspace.NAME])

    const modificationsAlreadyDrawn: paper.Item[] = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data) => data?.isModification && data?.opening_location_id
    )

    const results: (ProcessOverlayConflictsResult | null)[] = []

    // Keep a list of modifications already drawn to prevent redrawing
    // Calculate an initial list, which will be blank if this is
    // the first overlay but will be populated if we have returned to conflict
    // resolution and are adding another overlay see: https://portal.myparadigm.com/browse/BLUEPRINTS-23965
    const alreadyDrawnMaterialLocations = new Map<number, paper.Item>(
        modificationsAlreadyDrawn.map((item) => [item.data.opening_location_id, item])
    )

    for (const overlayId of overlayIds) {
        results.push(yield call(processOverlayConflicts, manager, overlayId, alreadyDrawnMaterialLocations))
    }

    const presentResults = results.filter((result): result is NonNullable<typeof result> => result !== null)
    const allConflicts = mergeConflictsByMaterialLocation(presentResults.flatMap((result) => result.conflicts))
    const anyHasConflicts = presentResults.some((result) => result.hasConflicts)
    // A null result means processOverlayConflicts couldn't actually check that overlay, not that
    // it has no conflicts -- treat it as unresolved.
    const anyOverlayUnchecked = results.some((result) => result === null)

    if (allConflicts.length) {
        yield put(
            setConflictedMaterialOptionModifications(
                allConflicts.map((mod) => ({
                    ...mod,
                    shapeType: undefined,
                    opening_group_id: undefined,
                }))
            )
        )
    }

    // Other materials are hidden temporarily on canvas. Preserve their visibility settings
    // so they return after Resolve Conflicts and manually hidden materials stay hidden.
    const overlappingDrawableIds = new Set(presentResults.flatMap((result) => result.overlappingDrawableIds ?? []))

    if (overlappingDrawableIds.size) {
        const visRecordUpdate = Array.from(overlappingDrawableIds).reduce((record, drawableId) => {
            record[drawableId] = true

            return record
        }, {} as GeometryVisibilityRecord)

        yield put(updateGeometryVisibility(visRecordUpdate))
    }

    if (!anyOverlayUnchecked && !anyHasConflicts) {
        yield put(updateConflictResolutionFlag(true))
    }
}

export function* watchForHighlightOptionConflictsForOverlays() {
    yield takeEvery(highlightOptionConflictsForOverlaysAction.type, handleHighlightOptionConflictsForOverlays)
}
